import { makeVar, useReactiveVar } from '@apollo/client';
import { Stack, useTheme } from '@fluentui/react';
import {
  addEdge,
  applyEdgeChanges,
  applyNodeChanges,
  Background,
  BackgroundVariant,
  Edge,
  Node,
  ReactFlow,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
  useReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import clsx from 'clsx';
import { DRAWER_CLOSE_WIDTH, DRAWER_OPEN_WIDTH } from 'common/constants';
import React, { useCallback, useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { Approval_approval_approvalSelectionConditions_nodes } from 'settings/account/approvalSetup/view/__generated__/Approval';
import { FlowApproval_approval_approvalSelectionConditions_nodes } from 'settings/account/intuitiveApprovalSetupFlow/__generated__/FlowApproval';
import { expandedNav } from 'utility/cache/ui';
import {
  CONDITION_NODE_WIDTH,
  REQUESTOR_INITIAL_VALUES,
} from '../../constants';
import { useStyles } from '../../index.styles';
import { ApprovalValues } from '../../types';
import { AddConditionNode } from '../Node/AddConditionNode';
import { CustomEdge } from '../Node/common/CustomeEdge';
import { ConditionNode } from '../Node/ConditionNode';
import TextUpdaterNode from '../Node/TextUpdater';
import { TitleNode } from '../Node/TitleNode';
import { convertToReactFlowNodes, getLayoutElements } from '../utils/layout';
import {
  getClosestEdgeUtil,
  getUpdatedEdgesWithReparenting,
} from '../utils/utils';
import { movingNodeId } from '../ApproverLevels';
import {
  CustomReactFlowControls,
  setInteractive,
} from '../../../../../../common/components/CustomReactFlowControls';
import { CloseEdge } from '../Node/common/CloseEdge';
import { ApprovalSetupFlowCommonData } from 'settings/account/intuitiveApprovalSetupFlow/__generated__/ApprovalSetupFlowCommonData';
import { getWindowDimensions } from 'common/utils/columnUtilities';

const edgeTypes = {
  'custom-edge': CustomEdge,
  'close-edge': CloseEdge,
};

const nodeTypes = {
  AddConditionNode,
  TitleNode,
  TextUpdaterNode,
  ConditionNode,
};

export type ConditionDataProps =
  FlowApproval_approval_approvalSelectionConditions_nodes;
type ConditionsProps = {
  approvalDetailsLoading: boolean;
  approvalTypes: ApprovalSetupFlowCommonData | undefined;
  isDisabled: boolean;
  isFlowUpdatable: boolean;
};

export const setNodeScrollState = makeVar<boolean>(true);
export const setFlowLayoutState = makeVar<'horizontal' | 'vertical'>(
  'vertical'
);
const ConditionsForm: React.FC<ConditionsProps> = (props) => {
  const styles = useStyles();
  const flowLayoutState = useReactiveVar(setFlowLayoutState);
  const isNodeScrollActive = useReactiveVar(setNodeScrollState);
  const theme = useTheme();
  const { watch, setValue } = useFormContext<ApprovalValues>();
  const conditionsFormData = watch('approvalSelectionConditions');
  const { getNodes, getInternalNode, setViewport } = useReactFlow();
  const [nodes, setNodes] = useNodesState<Node>([]);
  const [edges, setEdges] = useEdgesState<Edge>([]);
  const isInteractive = useReactiveVar(setInteractive);
  const renderRef = useRef(false);

  const onNodesChange = useCallback(
    (changes: any) => {
      const updated = applyNodeChanges(changes, getNodes());
      setNodes(updated);
    },
    [getNodes, setNodes]
  );

  const onEdgesChange = useCallback(
    (changes: any) => {
      const updated = applyEdgeChanges(changes, edges);
      setEdges(updated);
    },
    [edges, setEdges]
  );

  const onConnect = useCallback(
    (params: any) => {
      const updated = addEdge(params, edges);
      setEdges(updated);
    },
    [edges, setEdges]
  );

  const reactFlowStyles: React.CSSProperties | undefined = {
    backgroundColor: theme.palette.white,
  };

  const expanded = useReactiveVar(expandedNav);
  const { width } = getWindowDimensions();

  useEffect(() => {
    setNodeScrollState(true);
  }, []);

  const onLayout = (direction: 'TB' | 'LR', nodes: any, edges: any) => {
    const generatedLayout = getLayoutElements(nodes, edges, {
      direction,
    });
    setNodes([...generatedLayout.nodes]);
    setEdges([...generatedLayout.edges]);
  };
  useEffect(() => {
    if (conditionsFormData && props.approvalTypes) {
      const { nodes: modulatedNodes, edges: modulatedEdges } =
        convertToReactFlowNodes(
          conditionsFormData,
          props.approvalTypes,
          flowLayoutState,
          props.isDisabled,
          props.isFlowUpdatable
        );
      onLayout('TB', modulatedNodes, modulatedEdges);
      if (!renderRef.current && width) {
        setViewport({
          x: width ? width / 2 - CONDITION_NODE_WIDTH : 0,
          y: 80,
          zoom: 0.8,
        });
        renderRef.current = true;
      }
    }
  }, [conditionsFormData, flowLayoutState, props.approvalTypes]);

  const getClosestEdge = useCallback((node: any) => {
    const internalNode = getInternalNode(node.id); // your logic
    const existingNodes = getNodes(); // your logic
    return getClosestEdgeUtil(internalNode, existingNodes);
  }, []);

  const onNodeDrag = useCallback(
    (_: any, node: any) => {
      movingNodeId(node.data.id);

      if (node.type !== 'ConditionNode') return;
      const closeEdge = getClosestEdge(node);
      setEdges((es) => {
        const nextEdges = es.filter((e) => e.className !== 'temp');

        if (
          closeEdge &&
          !nextEdges.find(
            (ne) =>
              ne.source === closeEdge.source && ne.target === closeEdge.target
          )
        ) {
          closeEdge.className = 'temp';
          nextEdges.push(closeEdge);
        }

        return nextEdges;
      });
    },
    [getClosestEdge, setEdges]
  );

  function adjustEdges(
    edges: Edge[],
    newEdge: {
      id?: string;
      source: string;
      target: string;
      className?: string;
      type?: string;
      sourceHandle?: string;
      targetHandle?: string;
      animated?: boolean;
      label?: string;
    }
  ): Edge[] {
    const updatedEdges = getUpdatedEdgesWithReparenting(edges, newEdge);

    let conditionIndex = 1;
    const currentNodes = getNodes();
    const sortedByY = currentNodes.sort((a, b) => a.position.y - b.position.y);

    const updatedNodes = sortedByY.map((node) => {
      if (node.type === 'ConditionNode') {
        const updatedNode = {
          ...node,
          data: {
            ...node.data,
            importanceLevel: conditionIndex,
          },
        };
        conditionIndex++;
        return updatedNode;
      }
      return node;
    });

    const conditionNodeDataArray = updatedNodes
      .filter((node) => node.type === 'ConditionNode')
      .map((node) => node.data);

    setValue(
      'approvalSelectionConditions',
      [
        ...conditionNodeDataArray,
        REQUESTOR_INITIAL_VALUES,
      ] as Approval_approval_approvalSelectionConditions_nodes[],
      { shouldDirty: true }
    );

    return updatedEdges;
  }

  const onNodeDragStop = useCallback(
    (_: any, node: any) => {
      movingNodeId(undefined);

      if (node.type !== 'ConditionNode') return;
      const closeEdge = getClosestEdge(node);
      setEdges((es) => {
        const nextEdges = es.filter((e) => e.className !== 'temp');
        if (
          closeEdge &&
          !nextEdges.find(
            (ne) =>
              ne.source === closeEdge.source && ne.target === closeEdge.target
          )
        ) {
          // Remove the temporary edge if it exists
          const filteredEdges = nextEdges.filter(
            (e) =>
              !(e.source === closeEdge.source && e.target === closeEdge.target)
          );
          // Apply the edge reassignment algorithm
          const updatedEdges = adjustEdges(filteredEdges, closeEdge);
          setEdges(updatedEdges);
          onLayout('TB', getNodes(), updatedEdges);
          return updatedEdges;
        }
        setEdges(nextEdges);
        onLayout('TB', getNodes(), nextEdges);
        return nextEdges;
      });
    },
    [getClosestEdge]
  );
  return (
    <Stack
      className={clsx(styles.bodyContainer, styles.bodyContainerAlternative)}
    >
      <div
        style={{
          width: `calc(100% - ${
            expanded ? DRAWER_CLOSE_WIDTH : DRAWER_OPEN_WIDTH
          })`,
          height: 'calc(100vh - 250px)',
        }}
      >
        <ReactFlow
          // colorMode={isDarkMode ? 'dark' : 'light'}
          proOptions={{ hideAttribution: true }}
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onNodeDrag={onNodeDrag}
          onNodeDragStop={onNodeDragStop}
          onConnect={onConnect}
          onEdgesChange={onEdgesChange}
          nodeTypes={nodeTypes}
          style={reactFlowStyles}
          panOnScroll={isNodeScrollActive}
          panOnDrag={isNodeScrollActive || isInteractive}
          nodesDraggable={isInteractive}
          nodesConnectable={isInteractive}
          elementsSelectable={isInteractive}
          zoomOnScroll={isInteractive}
          zoomOnPinch={isInteractive}
          zoomOnDoubleClick={isInteractive}
          edgeTypes={edgeTypes}
        >
          {/* <Panel position="top-right">
            <button
              onClick={() => {
                setFlowLayoutState('vertical');
                onLayout('TB', nodesVarData, edgesVarData);
              }}
            >
              vertical layout
            </button>
            <button
              onClick={() => {
                setFlowLayoutState('horizontal');
                onLayout('LR', nodesVarData, edgesVarData);
              }}
            >
              horizontal layout
            </button>
          </Panel> */}
          {/* <Controls /> */}
          <Background
            variant={BackgroundVariant.Dots}
            gap={12}
            size={1}
            color={`${theme.palette.neutralDark}66`}
          />
          <CustomReactFlowControls />
        </ReactFlow>
      </div>
    </Stack>
  );
};

export const Conditions: React.FC<ConditionsProps> = (props) => {
  return (
    <ReactFlowProvider>
      <ConditionsForm {...props} />
    </ReactFlowProvider>
  );
};
